import clsx, { type ClassValue } from 'clsx';
import fetchRetry from 'fetch-retry';
import { ParseResultType, parseDomain } from 'parse-domain';
import { twMerge } from 'tailwind-merge';
import type {
  SubscriptionEntitlement,
  SubscriptionWithEntitlements,
} from '@/utils/stripe/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function absoluteUrl(path: string) {
  return `${process.env.NEXT_PUBLIC_SITE_URL}${path}`;
}

export async function fetcher<JSON>(
  input: RequestInfo,
  init?: RequestInit
): Promise<JSON> {
  const response = await fetch(input, { ...init, cache: 'no-store' });

  return response.json();
}

export function debounce<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
  immediate = true
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | undefined;
  let isInitialCall = true;

  return function (this: any, ...args: Parameters<T>) {
    const later = () => {
      timeoutId = undefined;
      if (!immediate) {
        callback.apply(this, args);
      }
    };

    const shouldCallNow = immediate && isInitialCall;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(later, delay);

    if (shouldCallNow) {
      isInitialCall = false;
      callback.apply(this, args);
    }
  };
}

export const shimmer = (w: number, h: number, color = '#f5f5f4') => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="${color}" offset="20%" />
      <stop stop-color="#222" offset="50%" />
      <stop stop-color="${color}" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="${color}" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
</svg>`;

export const toBase64 = (str: string) =>
  typeof window === 'undefined'
    ? Buffer.from(str).toString('base64')
    : window.btoa(str);

export const base64Shimmer = (width = 800, height = 800, color = '#f5f5f4') =>
  `data:image/svg+xml;base64,${toBase64(shimmer(width, height, color))}`;

// Tremor Raw focusInput [v0.0.1]

export const focusInput = [
  // base
  'focus:ring-2',
  // ring color
  'focus:ring-blue-200 focus:dark:ring-blue-700/30',
  // border color
  'focus:border-blue-500 focus:dark:border-blue-700',
];

// Tremor Raw focusRing [v0.0.1]

export const focusRing = [
  // base
  'outline outline-offset-2 outline-0 focus-visible:outline-2',
  // outline color
  'outline-blue-500 dark:outline-blue-500',
];

// Tremor Raw hasErrorInput [v0.0.1]

export const hasErrorInput = [
  // base
  'ring-2',
  // border color
  'border-red-500 dark:border-red-700',
  // ring color
  'ring-red-200 dark:ring-red-700/30',
];

const uuidRegex =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const isValidUuid = (uuid: string) => uuidRegex.test(uuid);

export const fetchWithRetry = fetchRetry(fetch, {
  retries: 2,
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30_000),
});

export function withTimeout<T>(
  asyncFn: () => Promise<T>,
  timeoutMs: number,
  timeoutMessage?: string
): Promise<T> {
  return new Promise((resolve, reject) => {
    // Set up the timeout
    const timeoutId = setTimeout(() => {
      reject(
        new Error(timeoutMessage || `Operation timed out after ${timeoutMs}ms`)
      );
    }, timeoutMs);

    // Execute the async function
    asyncFn()
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
}

/**
 * Sanitizes a string to be safe for use as a filename
 * @param filename - The filename to sanitize
 * @returns A sanitized filename safe for download
 */
export function sanitizeFilename(filename: string): string {
  return filename
    .toLowerCase()
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-z0-9._-]/g, '') // Remove any characters that aren't alphanumeric, dots, underscores, or hyphens
    .replace(/_{2,}/g, '_') // Replace multiple consecutive underscores with single underscore
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .substring(0, 100); // Limit length to prevent overly long filenames
}

export const isValidLink = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const parseResult = parseDomain(urlObj.hostname);
    return parseResult.type === ParseResultType.Listed;
  } catch {
    return false;
  }
};

/**
 * Creates a File object from a string
 * @param text - The text to create the file from
 * @param name - The name of the file
 * @returns A File object
 */
export const createFileFromText = (
  text: string,
  name = 'default-file-name.txt'
): File => {
  const blob = new Blob([text.trim()], { type: 'text/plain' });
  return new File([blob], name, { type: 'text/plain' });
};

/**
 * Gets a specific entitlement for the current org.
 * @param entitlementId - The id of the entitlement to get.
 * @param isBoolean - Whether to return a boolean (true/false) or number (quantity)
 * @returns true or false for boolean entitlements, or the quantity for quantity entitlements
 */
export const getEntitlement = <T extends boolean = false>(
  lookupKey: string,
  subscription: SubscriptionWithEntitlements,
  isBoolean = false as T
): T extends true ? boolean : number => {
  const entitlements = subscription?.entitlements;
  if (!entitlements) {
    return (isBoolean ? false : 0) as T extends true ? boolean : number;
  }

  // find the entitlement with the lookup key
  const entitlement = entitlements?.find(
    (feature: SubscriptionEntitlement) => feature.lookup_key === lookupKey
  );
  if (!entitlement) {
    return (isBoolean ? false : 0) as T extends true ? boolean : number;
  }

  if (isBoolean) {
    return (entitlement !== undefined) as T extends true ? boolean : number;
  }

  const quantity = (entitlement?.metadata as { quantity: number })?.quantity;
  return (quantity ?? 0) as T extends true ? boolean : number;
};
